import { type ChangeEvent, type Me, sessionSchema } from '@hugo/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ApiError, apiFetch } from './api'
import { authClient } from './auth-client'
import { connectLive, type LiveStatus } from './live'
import { onNativeSignIn } from './native-auth'
import { isNative } from './platform'
import { refresher } from './refresh'
import { serverOffset, userNow } from './time'
import { tokenStore } from './token-store'

export const meQueryKey = ['me'] as const

type SessionState = {
  status: 'loading' | 'signed-out' | 'signed-in' | 'error'
  me: Me | null
  live: LiveStatus
  /** The most recent change events (newest first), for the activity view. */
  recent: ChangeEvent[]
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  /** Server time minus device time (includes the test clock); see lib/time.ts. */
  nowOffsetMs: number
}

const SessionContext = createContext<SessionState | null>(null)

export function useSession(): SessionState {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionProvider>')
  return value
}

/**
 * The user's current time, computed at render and re-rendered every `tickMs`. Never display
 * `me.now`: it is the time when the session was fetched and goes stale.
 */
export function useUserNow(tickMs = 15_000): Date {
  const { nowOffsetMs } = useSession()
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), tickMs)
    return () => clearInterval(timer)
  }, [tickMs])
  return userNow(nowOffsetMs)
}

async function fetchMe(): Promise<Me | null> {
  if (isNative && !(await tokenStore.get())) return null
  try {
    const { me } = await apiFetch('/api/session', sessionSchema)
    if (!me && isNative) await tokenStore.clear()
    return me
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      if (isNative) await tokenStore.clear()
      return null
    }
    throw error
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const meQuery = useQuery({ queryKey: meQueryKey, queryFn: fetchMe, retry: 1, staleTime: 60_000 })
  const me = meQuery.data ?? null
  // Computed when a new session object arrives (right after the fetch or a clock change).
  const nowOffsetMs = useMemo(() => (me ? serverOffset(me.now) : 0), [me])
  const [live, setLive] = useState<LiveStatus>('offline')
  const [recent, setRecent] = useState<ChangeEvent[]>([])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: meQueryKey })
  }, [queryClient])

  // A native sign-in can finish while someone else is still signed in (another account, or a
  // test scenario via the debug link). Start from scratch: no cached data of the previous user,
  // and the app remounts (selection, open sheets) once the new session has loaded.
  useEffect(
    () =>
      onNativeSignIn((result) => {
        if (!result.ok) return
        setRecent([])
        void queryClient.resetQueries()
      }),
    [queryClient],
  )

  const signOut = useCallback(async () => {
    await authClient.signOut().catch(() => {})
    await tokenStore.clear()
    setRecent([])
    // Signed out first: data queries are disabled without a session, so removing them below
    // cannot trigger refetches that would fail with 401.
    queryClient.setQueryData(meQueryKey, null)
    await queryClient.cancelQueries()
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== meQueryKey[0] })
  }, [queryClient])

  const userId = me?.user.id
  // The feed cursor from when the session was loaded; read once, when the stream connects.
  const cursorRef = useRef<string | null>(null)
  cursorRef.current = me?.cursor ?? null
  useEffect(() => {
    if (!userId) {
      setLive('offline')
      return
    }
    // Changes from other tabs, devices, the agent or the worker refresh the affected views.
    // Bursts (a sync creating many items) collapse into one refetch per query family.
    let pending: ReturnType<typeof setTimeout> | null = null
    const stale = new Set<string>()
    const invalidate = (...roots: string[]) => {
      for (const root of roots) stale.add(root)
      if (pending) return
      pending = setTimeout(() => {
        pending = null
        // Waits for this tab's own changes in flight: they refresh once when done (refresh.ts).
        refresher.refresh(queryClient, [...stale])
        stale.clear()
      }, 80)
    }
    // Heads-ups follow from items: every item change may add or settle one.
    const refreshData = () => invalidate('items', 'insights', 'people', 'headsups')
    const stop = connectLive({
      // Start where the session was loaded: changes between loading and going live arrive as
      // events like any other (no blanket refetch that could race a change the user just made).
      after: cursorRef.current,
      onStatus: setLive,
      onEvent: (event) => {
        setRecent((prev) => [event, ...prev].slice(0, 50))
        if (event.type === 'clock.changed') {
          void queryClient.invalidateQueries({ queryKey: meQueryKey })
          refreshData()
        }
        if (/^(item|action|person)\./.test(event.type)) refreshData()
        // ai.status: a suggestion adds to today's AI cost; settings carry auto-filing.
        if (event.type === 'item.suggested') invalidate('ai')
        if (event.type === 'settings.changed') {
          void queryClient.invalidateQueries({ queryKey: ['settings'] })
          // Working days of time off follow the working hours and holidays.
          invalidate('ai', 'time-off')
          refreshData()
        }
        // Outbox changes move the sidebar count (items.counts lives under ['items']), and an
        // entry for a heads-up settles it.
        if (event.type.startsWith('outbox.')) invalidate('outbox', 'items', 'headsups')
        // Time off moves hugo's plan, and with it the heads-ups.
        if (event.type.startsWith('time_off.')) invalidate('time-off', 'items', 'headsups')
        if (event.type.startsWith('headsup.')) invalidate('headsups', 'items')
        // A sync brings items, and a calendar's busy time moves the plan (and its heads-ups) and
        // what hugo suggests as time off.
        if (event.type.startsWith('connection.')) {
          invalidate('connectors', 'items', 'headsups', 'time-off')
        }
      },
      onUnauthorized: () => {
        void signOut()
      },
    })
    return () => {
      if (pending) clearTimeout(pending)
      stop()
    }
  }, [userId, queryClient, signOut])

  const status: SessionState['status'] = meQuery.isPending
    ? 'loading'
    : meQuery.isError
      ? 'error'
      : me
        ? 'signed-in'
        : 'signed-out'

  return (
    <SessionContext.Provider value={{ status, me, live, recent, signOut, refresh, nowOffsetMs }}>
      {children}
    </SessionContext.Provider>
  )
}
